import React from 'react';
import { motion } from 'framer-motion';
import { Bike, Car, Hammer, GraduationCap, Gift, HeartPulse, Monitor, Plane, Edit3, TrendingUp, Crown, Sparkles, AlertTriangle, Users } from 'lucide-react';
import { fmt } from '@/lib/formatters';

const ICON_MAP = { Bike, Car, Hammer, GraduationCap, Gift, HeartPulse, Monitor, Plane };

const getProgressGradient = (pct) => {
  if (pct < 25) return 'from-red-500 to-orange-400';
  if (pct < 50) return 'from-orange-400 to-yellow-400';
  if (pct < 75) return 'from-yellow-400 to-lime-400';
  return 'from-lime-400 to-green-500';
};

const getPlanLabel = (goal) => {
  if (goal?.planName) return goal.planName;
  const isKC = goal?.modality === 'club' || goal?.subModality === 'grupal';
  return isKC ? 'Plan Acelerado' : 'Plan KOMPA+';
};

const isKompaPlus = (goal) => {
  if (goal?.planCode) return goal.planCode === 'kompa_plus';
  return goal?.modality !== 'club' && goal?.subModality !== 'grupal';
};

const isSmallClosedGroup = (goal) =>
  goal?.modality !== 'club' && goal?.subModality === 'grupal';

const MOCK_GROUP_MEMBERS = [];

export default function GoalCard({ goal, index, onEdit, inactive, bsRate, expectedGross }) {
  const Icon = ICON_MAP[goal.icon] || TrendingUp;
  const isPlus = isKompaPlus(goal);
  const planLabel = getPlanLabel(goal);
  const isGroup = isSmallClosedGroup(goal);

  // NET progress — savedAmount is the post-commission savings tracked by
  // the backend (Motor Flexible). Using the NET figure ensures the bar
  // never inflates with commissions (task 5 of the production sweep).
  // `expectedGross` is accepted to keep API compatibility with callers
  // that still pass it, but it is intentionally unused for progress now.
  void expectedGross;
  const netPct = goal.totalAmount > 0
    ? Math.min((Number(goal.savedAmount || 0) / Number(goal.totalAmount)) * 100, 100)
    : 0;

  // Vesting cashback (KOMPA+ only) — backend resets to 0 on inactive, but
  // we ALSO gate the UI so any stale state never leaks the value.
  const accumulatedCashback = Number(goal.accumulatedCashback) || 0;
  const cashbackPct = Number(goal.cashbackPct) || 0;
  const cashbackEligible = goal.cashbackEligible !== false && isPlus;
  const showCashback = cashbackEligible && !inactive && goal.status !== 'inactive' && accumulatedCashback > 0;

  const bonus = goal.bonus || {};
  const hasBonus = bonus.has_bonus || bonus.hasBonus;
  const maxBonus = bonus.max_bonus_eur || bonus.maxBonusEur || 0;
  const earnedBonus = goal.grossPaid > 0 && goal.totalAmount > 0
    ? Math.round(Math.min(goal.grossPaid / goal.totalAmount, 1.0) * maxBonus * 100) / 100
    : 0;
  const bonusPct = goal.totalAmount > 0 ? Math.min((goal.grossPaid / goal.totalAmount) * 100, 100) : 0;

  // Real members come from the API (backend joins group_invitations +
  // profiles by shared group_id). Fall back to an empty list so the
  // avatar strip simply hides when the group has no accepted members
  // yet (no more `MOCK_GROUP_MEMBERS` leak — task 12 hardcoding sweep).
  const members = Array.isArray(goal.members) ? goal.members : [];
  const memberColors = ['#fbb040', '#1f2d5a', '#4CAF50', '#E91E63', '#9C27B0', '#00BCD4'];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, type: 'spring', stiffness: 250, damping: 25 }}
      className={`bg-white rounded-2xl border shadow-sm hover:shadow-md transition-shadow overflow-hidden ${inactive ? 'border-red-200' : 'border-gray-100'}`}
      data-testid={`goal-card-${goal.id}`}
    >
      {/* Card header */}
      <div className="px-5 pt-5 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${inactive ? 'bg-red-50' : 'bg-[#fbb040]/15'}`}>
            <Icon className={`w-5 h-5 ${inactive ? 'text-red-400' : 'text-[#fbb040]'}`} />
          </div>
          <div>
            <p className="text-sm font-bold text-black">{goal.label}</p>
            <p className="text-[11px] text-gray-400 font-medium flex items-center gap-1" translate="no">
              {!isPlus ? (
                <><Crown className="w-3 h-3 text-[#1f2d5a]" /> {planLabel}</>
              ) : (
                <><Sparkles className="w-3 h-3 text-[#fbb040]" /> {planLabel}</>
              )}
            </p>
          </div>
        </div>
        {!inactive && onEdit && (
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(goal); }}
            className="text-xs font-medium text-[#1f2d5a] hover:underline flex items-center gap-1"
            data-testid={`edit-goal-${goal.id}`}
          >
            <Edit3 className="w-3 h-3" /> Editar
          </button>
        )}
      </div>

      {/* Inactive badge */}
      {inactive && (
        <div className="px-5 pb-2" data-testid={`inactive-badge-${goal.id}`}>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-50 text-red-600 text-[11px] font-bold rounded-full">
            <AlertTriangle className="w-3 h-3" /> Meta Inactiva
          </span>
        </div>
      )}

      {/* Amount focus */}
      <div className="px-5 pb-3">
        {inactive ? (
          <p className="text-3xl font-black text-red-600 tabular-nums tracking-tight" data-testid={`goal-amount-bs-${goal.id}`}>
            Bs. {fmt((goal.grossPaid || goal.payments?.reduce((s, p) => s + (p.amountSavings || p.amount || 0), 0) || 0) * (bsRate || 1))}
          </p>
        ) : (
          <p className="text-3xl font-black text-[#1f2d5a] tabular-nums tracking-tight" data-testid={`goal-amount-${goal.id}`}>
            &euro; {fmt(goal.totalAmount)}
          </p>
        )}
      </div>

      {/* Main progress bar */}
      <div className="px-5 pb-1">
        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
          <motion.div
            className={`h-full rounded-full bg-gradient-to-r ${inactive ? 'from-red-300 to-red-400' : getProgressGradient(netPct)}`}
            initial={{ width: 0 }}
            animate={{ width: `${netPct}%` }}
            transition={{ delay: 0.3 + index * 0.1, duration: 0.8, ease: 'easeOut' }}
          />
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-[11px] text-gray-400">{netPct.toFixed(1)}%</span>
          <span className={`text-[11px] font-medium ${inactive ? 'text-red-400' : 'text-[#fbb040]'} notranslate`} translate="no">{goal.frequency}</span>
        </div>
      </div>

      {/* KOMPA+ Vesting Cashback — locked while active, forfeited on inactive */}
      {showCashback && (
        <div className="px-5 pb-3" data-testid={`cashback-vesting-${goal.id}`}>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#fbb040]/10 border border-[#fbb040]/30 rounded-full">
            <Sparkles className="w-3 h-3 text-[#fbb040]" />
            <span className="text-[10px] font-bold text-[#1f2d5a] tabular-nums" translate="no">
              Cashback Acumulado: &euro; {fmt(accumulatedCashback)}
            </span>
            <span
              className="text-[9px] font-bold uppercase tracking-wider text-[#fbb040] flex items-center gap-0.5"
              title={`Recompensa ${(cashbackPct * 100).toFixed(1)}% — bloqueada hasta liquidación`}
            >
              · Bloqueado
            </span>
          </div>
        </div>
      )}

      {/* Legacy KOMPA+ projected bonus bar — only when cashback not yet active */}
      {isPlus && hasBonus && !inactive && !showCashback && cashbackPct > 0 && (
        <div className="px-5 pb-3" data-testid={`bonus-bar-${goal.id}`}>
          <div className="flex items-center gap-1.5 mb-1">
            <Sparkles className="w-3 h-3 text-[#fbb040]" />
            <span className="text-[10px] font-bold text-[#fbb040] uppercase tracking-wider" translate="no">
              Cashback hasta {(cashbackPct * 100).toFixed(1)}%
            </span>
            <span className="text-[10px] text-gray-400 ml-auto tabular-nums">
              &euro; {fmt(earnedBonus)} / &euro; {fmt(maxBonus)}
            </span>
          </div>
          <div className="w-full h-1.5 bg-[#fbb040]/10 rounded-full overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-[#fbb040]/60 to-[#fbb040]"
              initial={{ width: 0 }}
              animate={{ width: `${bonusPct}%` }}
              transition={{ delay: 0.5 + index * 0.1, duration: 0.8, ease: 'easeOut' }}
            />
          </div>
        </div>
      )}

      {/* Details row */}
      <div className="px-5 pb-3 flex gap-4 text-[11px] text-gray-400">
        <div>
          <span className="block text-gray-300 uppercase tracking-wider text-[9px] mb-0.5">Inicio</span>
          <span className="font-medium text-gray-600">{goal.startDate}</span>
        </div>
        <div>
          <span className="block text-gray-300 uppercase tracking-wider text-[9px] mb-0.5">Estimado</span>
          <span className="font-medium text-gray-600">{goal.estimatedEnd}</span>
        </div>
        {goal.status && goal.status !== 'active' && (
          <div>
            <span className="block text-gray-300 uppercase tracking-wider text-[9px] mb-0.5">Estado</span>
            <span className={`font-bold text-[11px] ${goal.status === 'threshold_65' ? 'text-blue-600' : goal.status === 'pending_payout' ? 'text-green-600' : goal.status === 'completed' ? 'text-green-700' : 'text-gray-600'}`}>
              {goal.status === 'threshold_65' ? 'Elegible 65%' : goal.status === 'pending_payout' ? 'Pendiente entrega' : goal.status === 'completed' ? 'Completada' : goal.status}
            </span>
          </div>
        )}
      </div>

      {/* Group member avatars (stacked, only for group goals) */}
      {isGroup && !inactive && members.length > 0 && (
        <div className="border-t border-gray-50 px-5 py-3" data-testid={`group-avatars-${goal.id}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center -space-x-2">
              {members.slice(0, 5).map((m, mi) => (
                <div
                  key={m.id || mi}
                  className="w-7 h-7 rounded-full border-2 border-white flex items-center justify-center text-[10px] font-bold text-white shadow-sm"
                  style={{ backgroundColor: memberColors[mi % memberColors.length] }}
                  title={m.nombre}
                >
                  {(m.nombre || 'K').charAt(0)}
                </div>
              ))}
              {members.length > 5 && (
                <div className="w-7 h-7 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-500 shadow-sm">
                  +{members.length - 5}
                </div>
              )}
            </div>
            <span className="text-[11px] text-gray-400 flex items-center gap-1">
              <Users className="w-3 h-3" /> {members.length} {members.length === 1 ? 'miembro' : 'miembros'}
            </span>
          </div>
        </div>
      )}
    </motion.div>
  );
}
